import { useState, useEffect } from "react";
import CustomGoogleMap from "./CustomGoogleMap";
import SearchResults from "./SearchResults";
import axios from "axios"; // Import axios for HTTP requests

export default function ProductLocationForm({ brandId, siteId }) {
  const [selectedProduct, setSelectedProduct] = useState(null); // Default to empty or initial value
  const [location, setLocation] = useState(null); // Placeholder for city/state/zip input
  const [radius, setRadius] = useState("");
  const [results, setResults] = useState([]); // Stores the API response
  const [products, setProducts] = useState([]); // Stores the product list
  const [error, setError] = useState(null); // Handles errors
  const [isFormSubmitted, setIsFormSubmitted] = useState(null); // Tracks form submission
  const [mapPins, setMapPins] = useState([]); // Pins to show on the map

  // Reset function to clear form and state
  const handleFormReset = () => {
    setSelectedProduct(""); // Reset the selected product
    setLocation(""); // Clear the location
    setRadius(""); // Reset the radius to default
    setResults([]); // Clear the results
    setMapPins([]); // Clear the map pins
    setError(null); // Clear any errors
    setIsFormSubmitted(false); // Reset form submission status
  };

  // Fetch the product list when the component is mounted
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(
          `https://api.b-fonline.com/api/brand/${brandId}`
        );
        setProducts(response.data.minorBrands); // Store the list of products
      } catch (err) {
        console.error("API request error:", err);
        setError("Failed to retrieve products. Please try again.");
      }
    };

    fetchProducts(); // Call the fetch function
  }, []); // Re-run effect if brandId changes

  const handleFormSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission

    // Convert radius to a number
    const numericRadius = parseInt(radius, 10);

    // Validate radius
    if (isNaN(numericRadius)) {
      setError("Please select a valid radius.");
      return;
    }

    const formData = {
      search_string: location,
      radius: numericRadius > 200 ? 200 : numericRadius, // Ensure radius is within the API limit
      type: "onpremise", // Assuming you want both on-premise and off-premise
      site_id: siteId,
      brand_id: brandId,
    };

    try {
      // Make the POST request to the API endpoint
      const response = await axios.post(
        "https://api.b-fonline.com/api/v2/retail_locations_by_search",
        formData
      );

      // throw an error if no locations are found
      if (!response.data || response.data.length === 0) {
        throw new Error("No locations found");
      }

      setResults(response.data); // Store the response data
      setMapPins(response.data); // Update the map pins
      setIsFormSubmitted(true); // Form submission successful
      setError(null); // Clear any errors
    } catch (err) {
      console.error("API request error:", err);
      setError("No locations found. Please try again.");
    }
  };

  return (
    <>
      {!isFormSubmitted && (
        <div className="wrapper">
          <div className="row">
            <div className="col">
              <form
                onSubmit={handleFormSubmit}
                className="product-locator-form"
              >
                {error && (
                  <div className="error-container">
                    <div className="error-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                      >
                        <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V152c0-13.3 10.7-24 24-24zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z" />
                      </svg>
                    </div>
                    <div className="error-message">
                      <p>{error}</p>
                    </div>
                  </div>
                )}

                <div className="field-group">
                  <label htmlFor="product-select">Select product:</label>
                  <select
                    id="product-select"
                    value={selectedProduct}
                    onChange={(e) => setSelectedProduct(e.target.value)}
                    aria-label="Select a product"
                    required
                  >
                    <option value="" disabled hidden>
                      Select a product
                    </option>

                    {products.map((product) => (
                      <option
                        key={product.brand_minor_id}
                        value={product.brand_minor_id}
                      >
                        {product.brand_minor_description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field-group">
                  <label htmlFor="location-input">
                    Enter your city, state, or zip:
                  </label>
                  <input
                    id="location-input"
                    type="text"
                    placeholder="Enter your location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    aria-label="City, State, or Zip"
                    required
                  />
                </div>
                <div className="field-group">
                  <label htmlFor="radius-select">
                    Select your radius (miles):
                  </label>
                  <select
                    id="radius-select"
                    value={radius}
                    onChange={(e) => setRadius(e.target.value)}
                    aria-label="Select radius in miles"
                    required
                  >
                    <option value="" disabled hidden>
                      Select a radius
                    </option>
                    <option value={5}>5 miles</option>
                    <option value={10}>10 miles</option>
                    <option value={20}>20 miles</option>
                  </select>
                </div>
                <div className="field-group">
                  <button
                    type="submit"
                    aria-label="Submit form"
                    className="btn"
                  >
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Only show map and results if form is submitted */}
      {isFormSubmitted && (
        <>
          <CustomGoogleMap pins={mapPins} /> {/* Pass map pins */}
          <SearchResults
            locations={results}
            handleFormReset={handleFormReset}
          />{" "}
          {/* Pass locations data */}
        </>
      )}
    </>
  );
}
